<!--
 * @Author: Tahiti
 * @Date: 2025-01-27 12:00:00
 * @LastEditors: Tahiti
 * @LastEditTime: 2026-01-06 10:59:11
 * @Description: Website Layout with Navigation and Breadcrumbs
 * @FilePath: /weryai-web/layouts/website.vue
-->
<template>
    <div class="website-layout">
        <nav class="main-nav" :class="{ 'main-nav--mobile-open': isMobileMenuOpen }">
            <div
                class="w-full mx-auto px-1 py-[10px] h-[52px] flex items-center justify-between md:px-5 backdrop-blur-[10px]">
                <!-- 左侧：Logo + 导航菜单 -->
                <div class="flex items-center gap-2 xl:gap-10">
                    <!-- Logo 区域 -->
                    <div class="logo md:ml-0 flex items-center gap-2">
                        <button
                            class="mobile-menu-toggle flex-shrink-0 xl:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-white/20 text-white hover:bg-white/10 transition-colors duration-200"
                            type="button" aria-label="Toggle navigation menu" @click="toggleMobileMenu">
                            <svg v-if="!isMobileMenuOpen" class="w-5 h-5" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <NuxtLink to="/" class="logo-link flex-shrink-0">
                            <img src="https://static.weryai.com/growth/image/weryai_5cb7d93ce37f69e6825b5bf4396a3d12.png"
                                alt="微鲤AI logo" class="h-[48px] w-auto" />
                        </NuxtLink>
                    </div>
                    <!-- 导航菜单（桌面端） -->
                    <ClientOnly>
                        <WebsiteDesktopNavMenu :video-model-list="videoModelList" :image-model-list="imageModelList"
                            @model-click="handleMenuLinkClick" />
                    </ClientOnly>
                </div>

                <!-- 右侧：语言选择 + 登录按钮 -->
                <ClientOnly>
                    <WebsiteNavActions :is-logged-in="isLoggedIn" :balance="balance" :user-info="userInfo"
                        :version="version" @coin-click="handleCoinClickToPageJump" @menu="handleMenuClick" />
                    <template #fallback>
                        <div v-if="LOGIN_ENTRY_ENABLED" class="nav-actions">
                            <div aria-label="User Login Button" tabindex="0" role="button" class="login-btn-custom"
                                @click="() => showLoginModal()">
                                {{ t('menus.login') }}
                            </div>
                        </div>
                    </template>
                </ClientOnly>
            </div>
        </nav>
        <WebsiteMobileMenu v-model:open="isMobileMenuOpen" />

        <main class="main-content max:md:mb-[80px]" :class="mainContentClass">
            <slot />
        </main>

        <mobile-footer-menu v-if="show_footer_menu" class="md:hidden block"></mobile-footer-menu>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import { useUserStore } from '@/stores/user'
import { showLoginModal } from '@/utils/emitter'
import { availableLanguages, deleteLangPrefix } from '@/utils/common'
import { useToolsHooks } from '@/utils/useHooks'
import { useAppStore } from '@/stores/app'
import { imageModelList, videoModelList } from '@/utils/website-model-lists'
import { LOGIN_ENTRY_ENABLED } from '@/utils/feature-flags'
const config = useRuntimeConfig()
const version = config.public.appVersion
const appStore = useAppStore()
const route = useRoute()
const { t } = useI18n()
const { handleMenuLinkClick } = useToolsHooks()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const balance = computed(() => userStore.balance || 0)
const userInfo = computed(() => userStore.userInfo)

const show_footer_menu = computed(() => {
    const canShowFooterMenu = ['/pricing', '/my-creations']
    const currentPath = deleteLangPrefix(route.path) || '/'

    return canShowFooterMenu.includes(currentPath)
})

// isHomePage
const isHomePage = computed(() => {
    const path = route.path

    if (path === '/' || path === '/index') {
        return true
    }

    const normalizedPath = path.replace(/\/$/, '')

    const supportedLanguages = availableLanguages.map(it => it.code)
    if (normalizedPath.startsWith('/') && normalizedPath.length > 1) {
        const langCode = normalizedPath.substring(1)
        if (supportedLanguages.includes(langCode)) {
            return true
        }
    }

    return false
})

// =========================
// 移动端顶部菜单
// =========================
const isMobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
}

// handle menu item click
const handleMenuClick = (action: string) => {
    if (action === 'logout') {
        userStore.logout()
        appStore.clearRecentCreatedOrder()
        // @ts-ignore
        navigateTo('/')
        return
    } else if (action === 'profile') {
        emitter.emit('open-user-profile-modal')
    } else {
        // @ts-ignore
        navigateTo('/' + action)
    }
}

// 说明：曾经存在一个“用户菜单点击”的独立处理函数，但现在已由 `WebsiteNavActions` 统一通过事件回传
// check if show breadcrumb (exclude home and pricing page)
const shouldShowBreadcrumb = computed(() => {
    const isPricing =
        route.path === '/pricing' || /^\/[a-z]{2}\/pricing$/.test(route.path)
    const isPrivacyPolicy =
        route.path === '/privacy-policy' ||
        /^\/[a-z]{2}\/privacy-policy$/.test(route.path)
    const isTermsAndConditions =
        route.path === '/terms-and-conditions' ||
        /^\/[a-z]{2}\/terms-and-conditions$/.test(route.path)
    const isRefundPolicy =
        route.path === '/refund-policy' ||
        /^\/[a-z]{2}\/refund-policy$/.test(route.path)
    const isFaq = route.path === '/faq' || /^\/[a-z]{2}\/faq$/.test(route.path)
    const isAboutUs =
        route.path === '/about-us' || /^\/[a-z]{2}\/about-us$/.test(route.path)
    // 邀请落地页不需要 breadcrumb 和顶部 padding
    const isInvitation =
        route.path === '/invitation' || /^\/[a-z]{2}\/invitation$/.test(route.path)

    return (
        !isHomePage.value &&
        !isPricing &&
        !isPrivacyPolicy &&
        !isTermsAndConditions &&
        !isRefundPolicy &&
        !isFaq &&
        !isAboutUs &&
        !isInvitation
    )
})

const pricePage = computed(() => {
    // Match /pricing, /zh/pricing, /en/pricing, /jp/pricing, etc.
    return route.path === '/pricing' || /^\/[a-z]{2}\/pricing$/.test(route.path)
})

const isModelIntroPage = computed(() => {
    const path = route.path
    return /^\/(?:[a-z]{2}\/)?(image|video)\//.test(path)
})

const is_new_ui = computed(() => {
    const path: any = route.path
    if (path.includes('/video/runway-ai')) {
        return true
    } else if (path.includes('/video/runway-ai-4-5')) {
        return true
    } else if (path.includes('/video/hailuo-ai')) {
        return true
    } else if (path.includes('/video/hailuo-23')) {
        return true
    } else if (path.includes('/video/sora/sora-2')) {
        return true
    } else if (path.includes('/video/veo/veo-3-1')) {
        return true
    } else if (path.includes('/video/veo/veo-3')) {
        return true
    } else if (path.includes('/video/pika-2-2')) {
        return true
    } else if (path.includes('/image/weryai-image-2')) {
        return true
    } else if (path.includes('/video/weryai-video-1')) {
        return true
    } else if (path.includes('/image/grok-2')) {
        return true
    }
    return false
})

/**
 * 模型介绍页背景图预加载（仅在真正需要时注入）。
 *
 * 背景：
 * - 之前放在 `nuxt.config.ts` 的全局 `app.head.link` 里，会导致“首页也预加载一张很大的背景图”，
 *   在移动端会抢带宽、影响 LCP。
 * - 这里改为：仅当当前路由确实是模型介绍页且会展示背景图时才 preload。
 * - `useHead` 在 Nuxt 中支持 SSR 输出，确保首屏 HTML 就带上 preload。
 */
useHead(() => {
    const shouldPreloadBg = !is_new_ui.value && isModelIntroPage.value
    return {
        link: shouldPreloadBg
            ? [
                {
                    rel: 'preload',
                    as: 'image',
                    href: 'https://static.weryai.com/growth/image/2af2def31fbf1a73e25e49c6d64b8356.webp',
                    fetchpriority: 'high',
                },
            ]
            : [],
    }
})

const mainContentClass = computed(() => ({
    'pt-24': shouldShowBreadcrumb.value,
    showBgImg: !is_new_ui.value && isModelIntroPage.value,
    'main-box !pt-[52px]': is_new_ui.value && isModelIntroPage.value,
}))

// generate breadcrumb title based on route
const breadcrumbTitle = computed(() => {
    const path = route.path

    // video model page
    if (path.includes('/video/veo/veo-3-1')) return t('veo31Link')
    if (path.includes('/video/veo/veo-3')) return t('veo3')
    if (path.includes('/video/veo/veo-3-fast')) return t('veo3Fast')
    if (path.includes('/video/sora/sora-2')) return t('sora2')
    if (path.includes('/video/kling-ai')) return 'Kling 2.5'
    if (path.includes('/video/runway-ai')) return 'Runway'
    if (path.includes('/video/runway-ai—4-5')) return 'RUNWAY_4_5'
    if (path.includes('/video/hailuo-ai') || path.includes('/video/hailuo-23'))
        return 'Hailuo AI'
    if (path.includes('/video/seedance')) return 'Seedance'
    if (path.includes('/video/wan-ai')) return 'Wan AI'

    // image model page
    if (path.includes('/image/nano-banana')) return 'Nano Banana'
    if (path.includes('/image/flux')) return 'FLUX'
    if (path.includes('/image/imagen')) return 'Imagen'
    if (path.includes('/image/gpt-4o')) return 'GPT-4o'
    if (path.includes('/image/flux-kontext')) return 'Flux Kontext'
    if (path.includes('/image/qwen-image')) return 'Qwen Image'
    if (path.includes('/image/wan-ai')) return 'Wan AI'
    if (path.includes('/image/seedream')) return 'Seedream 4.0'
    if (path.includes('/image/dreamina')) return 'Dreamina'

    // default return the last part of the path
    return path.split('/').pop() || ''
})

// scroll 性能优化：避免每次滚动都 querySelector + 写 style（会触发样式计算/重绘）
let navBarEl: HTMLElement | null = null
let lastIsScrolled = false
let scrollRafId: number | null = null
let shouldEnableBlur = true

const updateNavBackdrop = () => {
    if (typeof window === 'undefined') return
    if (!navBarEl) return

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const isScrolled = scrollTop > 20

    // 只有状态发生变化时才更新，避免每次 scroll 都触发样式写入
    if (isScrolled === lastIsScrolled) return
    lastIsScrolled = isScrolled

    // 用 class 切换，避免每次滚动写 style（更利于浏览器做样式/合成优化）
    navBarEl.classList.toggle('main-nav--scrolled', isScrolled)
    navBarEl.classList.toggle(
        'main-nav--scrolled--no-blur',
        isScrolled && !shouldEnableBlur
    )
}

const handleScrollPage = () => {
    if (typeof window === 'undefined') return
    if (scrollRafId != null) return

    scrollRafId = window.requestAnimationFrame(() => {
        scrollRafId = null
        updateNavBackdrop()
    })
}

// 说明：历史遗留的 dropdown 状态（language/video/image/tools）已迁移到子组件，layout 不再维护这些状态

onMounted(() => {
    if (typeof window !== 'undefined') {
        navBarEl = document.querySelector('.main-nav') as HTMLElement | null

        // 低端/省流/省电降级：禁用 blur，保留半透明底色
        // - deviceMemory <= 4：更可能是低端设备
        // - saveData：用户明确省流
        // - prefers-reduced-motion：通常也意味着希望减少动态/特效
        const navAny = navigator as any
        const deviceMemory = Number(navAny?.deviceMemory || 0)
        const saveData = !!navAny?.connection?.saveData
        const prefersReducedMotion =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        shouldEnableBlur = !(saveData || prefersReducedMotion || deviceMemory > 0 && deviceMemory <= 4)

        // 首次同步一次，确保刷新在非顶部时样式正确
        updateNavBackdrop()
        window.addEventListener('scroll', handleScrollPage, { passive: true })
    }
})

onUnmounted(() => {
    if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScrollPage)
        if (scrollRafId != null) {
            window.cancelAnimationFrame(scrollRafId)
            scrollRafId = null
        }
        navBarEl = null
    }
})

// 路由变化时关闭移动端菜单，防止残留
watch(
    () => route.path,
    () => {
        isMobileMenuOpen.value = false
    }
)

// 控制移动端菜单打开时锁定页面滚动
watch(
    () => isMobileMenuOpen.value,
    newVal => {
        if (typeof document === 'undefined') return
        document.body.style.overflow = newVal ? 'hidden' : 'auto'
    }
)

// 页面跳转
const handleCoinClickToPageJump = () => {
    if (balance.value == 0) {
        // 余额为0，跳转到 pricing 页面并显示按年计费
        navigateTo('/pricing?tab=yearly')
    } else {
        // 有余额，跳转到 pricing 页面并显示按积分收费
        navigateTo('/pricing?tab=credits')
    }
}
</script>

<style lang="less" scoped>
.website-layout {
    min-height: 100vh;
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: #0d0e12;
}

/* 粉色背景已移除，避免在登录状态下刷新时显示 */

.main-content {
    position: relative;
    z-index: 1;
    background-color: #0d0e12;
}

/* LCP 优化：背景图使用 <img> 元素实现，而非 CSS background-image */
.main-content.has-bg-img {
    position: relative;
}

.showBgImg {
    background-image: url('https://static.weryai.com/growth/image/2af2def31fbf1a73e25e49c6d64b8356.webp');
    background-size: contain;
    background-repeat: no-repeat;
    background-position-y: 52px;
}

.main-nav {
    background: transparent;
    border-bottom: none;
    width: 100%;
    position: fixed;
    top: 0;
    z-index: 40;
}

.main-nav--scrolled {
    /* 背景 + 模糊，避免仅 blur 导致的可读性问题 */
    background-color: rgba(13, 14, 18, 0.55);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    /* 仅在滚动状态启用 will-change，减少常驻合成层开销 */
    will-change: backdrop-filter;
}

/* 降级：禁用 blur（低端/省流/偏好减少动效），仍保留底色 */
.main-nav--scrolled--no-blur {
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    will-change: auto;
}

@media (prefers-reduced-motion: reduce) {
    .main-nav--scrolled {
        will-change: auto;
    }
}

.main-nav--mobile-open {
    background-color: #0d0e12;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.logo-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
}

/* 确保弹窗和触发元素之间的连接 */
:deep(.video-pop) {
    z-index: 2000 !important;
    pointer-events: auto !important;
    margin-top: 8px !important;

    /* 添加一个透明的连接区域，让鼠标可以平滑移动到弹窗 */
    &::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 0;
        right: 0;
        height: 8px;
        background: transparent;
    }
}

:deep(.el-popper.is-pure) {
    pointer-events: auto;
}

.nav-actions {
    display: flex;
    align-items: center;
}

.login-btn-custom {
    background-color: #99f768;
    color: #000;
    padding: 6px 20px;
    border-radius: 8px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}
</style>
